import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaHotAirBalloon } from "react-icons/fa";
import "../css/styles.css";

const MasaiMaraSafari = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348869/3_Days_Masai_mara_Express_Safari_2_gjcw8a.jpg"
            alt="Masai Mara Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Kenya Safari</span>
          <h1>3 Day Masai Mara Express Safari</h1>
          <p className="safari-detail-hero-sub">
            Sometimes a few days are enough to experience something unforgettable.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>A True Taste of Africa's Wild Heart</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This short safari takes you from the lively city of Nairobi to the wide-open plains of the Masai Mara, one of Africa's most famous wildlife reserves. In just three days, you will enter a world where lions rest in the tall grass, elephants wander slowly across the savannah, and herds of zebras and wildebeest stretch across the horizon.
          </p>
          <p>
            The journey itself is part of the adventure. As you leave Nairobi, the road drops into the beautiful Great Rift Valley before continuing through small towns and rolling countryside.
          </p>
          <p>
            Once inside the Masai Mara, every game drive becomes a chance to witness nature in its purest form. The Mara is home to an incredible variety of wildlife, including lions, leopards, elephants, buffalo, giraffes, and countless antelope.
          </p>
          <p>
            Though the safari is short, the experience is rich, offering a true taste of Africa's wild heart.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Scenic drive through the Great Rift Valley</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Two exciting days of wildlife viewing in the Masai Mara</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Chance to see the Big Five in their natural habitat</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Endless golden savannah landscapes</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Optional visit to a traditional Maasai village</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>3 Days / 2 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Destination</h4>
            <p>Masai Mara National Reserve</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>All year round</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Nairobi to Masai Mara</h3>
              <p>
                Your adventure begins in the morning as you depart Nairobi and head towards the Masai Mara. Along the way, you will stop at a viewpoint overlooking the vast Great Rift Valley, a dramatic landscape that stretches for thousands of kilometers across Africa.
              </p>
              <p>
                The drive continues through the countryside where small towns and farms give way to the wide-open plains of the Mara. You will arrive at your lodge or camp in time for lunch. After some time to relax, your first afternoon game drive begins.
              </p>
              <p>
                As the sun lowers over the savannah, animals start to appear. You may see elephants moving through the grasslands, giraffes browsing on acacia trees, and perhaps even lions preparing for the night ahead.
              </p>
              <p className="safari-detail-day-note">Overnight in the Masai Mara.</p>
            </div>
          </div>

          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Full Day in the Masai Mara</h3>
              <p>
                Today is a full day to explore the wonders of the Masai Mara. You will head out early in the morning when the savannah is still cool and quiet. This is one of the best times to see wildlife as many animals are active before the heat of the day.
              </p>
              <p>
                The Masai Mara is famous for its incredible concentration of wildlife. Lions are often seen resting in the shade while cheetahs scan the plains for prey. Herds of zebras and antelopes move across the grasslands, always alert for predators.
              </p>
              <p>
                If traveling during the migration season, you may witness the great herds of wildebeest and zebras that cross the Mara in search of fresh grass.
              </p>
              <p>
                After lunch and some time to relax at the camp, another game drive takes place in the afternoon, offering even more chances to experience the magic of the African wilderness.
              </p>
              <p className="safari-detail-day-note">Overnight in the Masai Mara.</p>
            </div>
          </div>

          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Masai Mara to Nairobi</h3>
              <p>
                After breakfast, enjoy one last look at the peaceful landscapes of the Mara before beginning the journey back to Nairobi.
              </p>
              <p>
                The return drive offers time to reflect on the sights and moments of the past few days — the wildlife encounters, the vast landscapes, and the quiet beauty of the savannah.
              </p>
              <p>
                Arrival in Nairobi will be in the afternoon where you will be dropped off at your hotel or at the airport depending on your travel plans.
              </p>
              <p className="safari-detail-day-note">
                Though the safari may be short, the memories of the Masai Mara will remain long after the journey ends.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Rhino Tourist Camp</li>
              <li><FaHotel /> Enkorok Mara Camp</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Zebra Plains Mara Camp</li>
              <li><FaHotel /> Mara Sopa Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Mara Serena Safari Lodge</li>
              <li><FaHotel /> Sarova Mara Game Camp</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== OPTIONAL EXPERIENCES ===== */}
      <section className="safari-detail-optional">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Add-On Experiences</span>
          <h2>Optional Experiences in the Masai Mara</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-optional-grid">
          <div className="safari-detail-optional-card">
            <FaMapMarkerAlt className="safari-detail-optional-icon" />
            <h3>Maasai Village Visit</h3>
            <p>
              Meet members of the Maasai community and learn about their way of life. The Maasai are known for their colorful clothing, strong traditions, and deep connection to the land. During the visit, you may see their homes, listen to stories about their culture, and experience traditional songs or dances.
            </p>
          </div>
          <div className="safari-detail-optional-card">
            <FaStar className="safari-detail-optional-icon" />
            <h3>Hot Air Balloon Safari</h3>
            <p>
              Before sunrise, rise gently above the Mara plains in a hot air balloon as the landscape slowly awakens below you. From the sky, you may see elephants, giraffes, and herds of antelope moving across the savannah. After landing, the experience often ends with a special bush breakfast served in the wild — a peaceful and magical way to start the day.
            </p>
          </div>
        </div>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport or hotel pickup in Nairobi</li>
              <li>Transport in a comfortable safari vehicle with a professional guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as Maasai village visits or hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This short safari can easily be extended with more days in the Masai Mara or combined with other destinations such as Lake Nakuru, Amboseli, or the Kenyan coast. Tell us your travel plans and we will design a journey that fits you perfectly.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=3 Day Masai Mara Express Safari" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MasaiMaraSafari;
